import z from "zod";

import { Environment, Priority, Status, TicketType } from "@/features/tickets/types";

export const createTicketSchema = z.object({
  projectId: z.string(),

  title: z.string().min(1, "Title is required").max(128, "Title is too long, it must not exceed 128 characters"),

  type: z.enum(["Bug", "Feature", "Task", "Improvement"] as const satisfies readonly TicketType[]),

  priority: z.enum(["Critical", "High", "Medium", "Low"] as const satisfies readonly Priority[]),

  description: z.string().max(5000, "Description is too long, it must not exceed 5000 characters").optional(),

  assignedToId: z.string().optional(),

  tags: z.array(z.string().max(32, "Each tag must not exceed 32 characters")),

  effort: z.number().int().positive("Effort must be a positive number").optional(),

  reproducingSteps: z.string().max(3000, "Reproducing steps must not exceed 3000 characters").optional(),

  expectedBehaviour: z.string().max(1500, "Expected behaviour must not exceed 1500 characters").optional(),

  actualBehaviour: z.string().max(1500, "Actual behaviour must not exceed 1500 characters").optional(),

  affectedVersion: z.string().max(64, "Affected version must not exceed 64 characters").optional(),

  environment: z.enum(["Production", "Staging", "Development", "Local"] as const satisfies readonly Environment[]).optional(),
});

export type CreateTicketSchema = z.infer<typeof createTicketSchema>;

export const editTicketSchema = z.object({
  projectId: z.string(),

  ticketId: z.string(),

  title: z.string().min(1, "Title is required").max(128, "Title is too long, it must not exceed 128 characters"),

  type: z.enum(["Bug", "Feature", "Task", "Improvement"] as const satisfies readonly TicketType[]),

  status: z.enum(["Open", "In Progress", "In Review", "Resolved", "Closed"] as const satisfies readonly Status[]),

  priority: z.enum(["Critical", "High", "Medium", "Low"] as const satisfies readonly Priority[]),

  description: z.string().max(5000, "Description is too long, it must not exceed 5000 characters").optional(),

  assignedToId: z.string().nullable().optional(),

  tags: z.array(z.string().max(32, "Each tag must not exceed 32 characters")),

  effort: z.number().int().positive("Effort must be a positive number").optional(),

  reproducingSteps: z.string().max(3000, "Reproducing steps must not exceed 3000 characters").optional(),

  expectedBehaviour: z.string().max(1500, "Expected behaviour must not exceed 1500 characters").optional(),

  actualBehaviour: z.string().max(1500, "Actual behaviour must not exceed 1500 characters").optional(),

  affectedVersion: z.string().max(64, "Affected version must not exceed 64 characters").optional(),

  environment: z.enum(["Production", "Staging", "Development", "Local"] as const satisfies readonly Environment[]).optional(),
});

export type EditTicketSchema = z.infer<typeof editTicketSchema>;

export const deleteTicketSchema = z.object({
  projectId: z.string(),
  ticketId: z.string(),
});

export type DeleteTicketSchema = z.infer<typeof deleteTicketSchema>;

export const createCommentSchema = z.object({
  projectId: z.string(),

  ticketId: z.string(),

  body: z.string().min(1, "Comment is required").max(5000, "Comment must not exceed 5000 characters"),
});

export type CreateCommentSchema = z.infer<typeof createCommentSchema>;

export const createReplySchema = z.object({
  projectId: z.string(),

  ticketId: z.string(),

  commentId: z.string(),

  body: z.string().min(1, "Reply is required").max(5000, "Reply must not exceed 5000 characters"),
});

export type CreateReplySchema = z.infer<typeof createReplySchema>;

export const updateCommentSchema = z.object({
  projectId: z.string(),

  ticketId: z.string(),

  commentId: z.string(),

  body: z.string().min(1, "Comment is required").max(5000, "Comment must not exceed 5000 characters"),
});

export type UpdateCommentSchema = z.infer<typeof updateCommentSchema>;

export const deleteCommentSchema = z.object({
  projectId: z.string(),
  ticketId: z.string(),
  commentId: z.string(),
});

export type DeleteCommentSchema = z.infer<typeof deleteCommentSchema>;

export const likeCommentSchema = z.object({
  projectId: z.string(),
  ticketId: z.string(),
  commentId: z.string(),
});

export type LikeCommentSchema = z.infer<typeof likeCommentSchema>;
